import type { ReactNode } from 'react'
import { IconRestore, IconTrash } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { errorMessage } from '@/api/client'
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States'
import { tr } from '@/i18n/tr'
import { Button, Drawer, notifications } from '@/ui'

/** Çöp kutusunda listelenen tek kayıt. */
export interface TrashRow {
  id: string
  /** Başlık — not adı, dosya adı, proje adı. */
  label: string
  /** İkinci satır: silinme zamanı gibi bağlam. */
  hint?: string
  /** Satırın solundaki karo (proje monogramı, dosya türü). */
  leading?: ReactNode
}

/**
 * Çöp kutusu çekmecesi.
 *
 * <p>Silme onayları kullanıcıya açıkça "çöp kutusuna taşınacak" diyor; bu
 * yüzden çöp kutusuna ulaşılabilir bir yer olmak ZORUNDA. Aksi halde
 * uygulama geri alınabilir bir işlem vaat edip geri almayı imkânsız kılar.
 *
 * <p>Not, dosya ve proje için aynı bileşen kullanılır: üçünün de davranışı
 * "listele + geri yükle"dir, ayrı ekranlar yazmak üçe katlanan bakım demekti.
 */
export function TrashDrawer({
  opened, onClose, title, queryKey, load, restore, onRestored,
}: {
  opened: boolean
  onClose: () => void
  title: string
  queryKey: unknown[]
  load: () => Promise<TrashRow[]>
  restore: (id: string) => Promise<unknown>
  onRestored?: () => void
}) {
  const qc = useQueryClient()

  const query = useQuery({
    queryKey,
    queryFn: load,
    // Çekmece kapalıyken istek atılmaz; açılınca taze veri gelir.
    enabled: opened,
    staleTime: 0,
  })

  const geriYukle = useMutation({
    mutationFn: restore,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey })
      onRestored?.()
      notifications.show({ color: 'green', message: 'Geri yüklendi.' })
    },
    onError: (e) => notifications.show({ color: 'red', message: errorMessage(e) }),
  })

  return (
    <Drawer opened={opened} onClose={onClose} title={title} width={440}>
      {query.isLoading && <LoadingState />}
      {query.isError && <ErrorState error={query.error} onRetry={() => query.refetch()} />}

      {query.data?.length === 0 && (
        <EmptyState title="Çöp kutusu boş." hint="Sildiğiniz kayıtlar burada birikir ve buradan geri alınabilir."
          icon={<IconTrash size={20} stroke={1.6} />} />
      )}

      {!!query.data?.length && (
        <div className="nx-trash">
          {query.data.map((row) => (
            <div key={row.id} className="nx-trash__row">
              {row.leading && <span className="nx-trash__lead">{row.leading}</span>}
              <span className="nx-trash__text">
                <span className="nx-trash__name">{row.label}</span>
                {row.hint && <span className="nx-trash__hint">{row.hint}</span>}
              </span>
              <Button size="sm" variant="secondary" leftIcon={<IconRestore size={15} stroke={1.8} aria-hidden="true" />}
                loading={geriYukle.isPending && geriYukle.variables === row.id}
                onClick={() => geriYukle.mutate(row.id)}>
                {tr.common.restore}
              </Button>
            </div>
          ))}
          <p className="nx-trash__note">Sildiğiniz kayıtlar burada birikir ve buradan geri alınabilir.</p>
        </div>
      )}
    </Drawer>
  )
}
